import { call, put, takeLatest, takeEvery, take, select } from 'redux-saga/effects'
import type { PayloadAction } from '@reduxjs/toolkit'
import { collection, query, where, doc, writeBatch, updateDoc, deleteDoc, serverTimestamp, arrayUnion, arrayRemove, Timestamp } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { createSnapshotChannel } from '../utils/createSnapshotChannel'
import { showToast } from '@/components/app/Toast/showToast'
import { db, functions } from '../../lib/firebase'
import { history } from '../../lib/history'
import { selectIsWorkspacePro, selectWorkspace, selectMembers } from '../workspace/workspaceSelectors'
import { selectAuth } from '../auth/authSelectors'
import { selectProjectsAllIds, selectMemberProjectIds, selectProjectById, selectProjectsById } from './projectsSelectors'
import type { RootState } from '../index'
import {
    createProject as createProjectAction,
    updateProjectNameSaga,
    updateProjectDescriptionSaga,
    deleteProjectSaga,
    publishProjectSaga,
    unpublishProjectSaga,
    addMemberSaga,
    removeMemberSaga,
    inviteToProjectSaga,
    updateProjectOwnerSaga,
    setProjectInviteLoading,
    setProjectInviteError,
    upsertProject,
    removeProject,
    setMemberProjectIds,
    setLoading,
    setCreatingProjectId,
} from './projectsSlice'
import { addListLocal } from '../lists/listsSlice'
import {addLoadingKey, removeLoadingKey, setOpenModal} from '../ui/uiSlice'
import type { Project } from './projectsSlice'
import { logTiming } from '@/utils/timing'
import {FREE_PLAN_PROJECTS_LIMIT} from "@/const/constants.ts";
import { AnalyticsEvent, trackEvent, setPersonProps } from '@/lib/analytics'

// Create project
function* createProjectWorker(action: PayloadAction<{ name: string; workspaceId: string; userId: string }>): Generator<any, void, any> {
    const { name, workspaceId, userId } = action.payload
    const projectId: string = yield call(() => doc(collection(db, 'projects')).id)

    const isProPlan: boolean = yield select(selectIsWorkspacePro)
    const projectIds: string[] = yield select(selectProjectsAllIds)
    const projectCount = projectIds.length
    if (!isProPlan && projectCount >= FREE_PLAN_PROJECTS_LIMIT) {
        // Paywall funnel: blocked intent. Authed users only — guests have no plan to upgrade.
        const authed = yield select(selectAuth)
        if (authed) trackEvent(AnalyticsEvent.LimitHit, { limit: 'projects', workspace_id: workspaceId })
        yield put(setOpenModal({ type: 'upgradeToPro' }))
        return
    }

    const authState = yield select(selectAuth)
    if (!authState) {
        // Guest mode: create locally
        const listId = `list-${Date.now()}-0`
        yield put(upsertProject({
            id: projectId,
            workspaceId,
            ownerId: '',
            name,
            description: '',
            shared_members: [userId],
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
        } as Project))
        const memberProjectIds: string[] = yield select(selectMemberProjectIds)
        yield put(setMemberProjectIds([...memberProjectIds, projectId]))
        yield put(addListLocal({
            id: listId,
            projectId,
            workspaceId,
            name: '',
            order: 0,
            createdAt: Timestamp.now(),
        }))
        showToast('Project created')
        return
    }

    // Mark this project as "being created" so WorkspaceProjectsPage filters it from the list
    // even after listenAllProjects fires during the Firestore write (snapshot fires before commit resolves).
    yield put(setCreatingProjectId(projectId))
    yield put(addLoadingKey('create-project'))
    try {
        // Only the creator is set here (the create rule requires the author to be in
        // shared_members). The default "open to the whole workspace" membership is
        // applied server-side by the onProjectCreated trigger, so every creation path
        // (web app, public API, MCP) shares projects the same way.
        const batch = writeBatch(db)

        const projectRef = doc(db, 'projects', projectId)
        batch.set(projectRef, {
            workspaceId,
            ownerId: userId,
            name,
            description: null,
            members: [],
            shared_members: [userId],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        })

        const listRef = doc(collection(db, 'lists'))
        batch.set(listRef, {
            projectId,
            workspaceId,
            name: '',
            order: 0,
            createdAt: serverTimestamp()
        })

        // Firestore's onSnapshot fires before commit resolves (local write is immediate).
        // By the time we navigate, project + list are already in Redux via listenAllProjects/listenAllLists.
        yield call([batch, batch.commit])

        // Activation funnel: project created. PostHog counts the first occurrence
        // per user for the funnel, so a generic event (no "first" gating) is enough.
        trackEvent(AnalyticsEvent.ProjectCreated, { project_id: projectId })

        const workspace = yield select(selectWorkspace)
        history.push(`/${workspace?.workspaceUrlId}/projects/${projectId}`)
        showToast('Project created')
    } catch (err) {
        console.error('Error creating project:', err)
    } finally {
        yield put(setCreatingProjectId(null))
        yield put(removeLoadingKey('create-project'))
    }
}

// Update project name
function* updateProjectNameWorker(action: PayloadAction<{ projectId: string; name: string }>): Generator<any, void, any> {
    const { projectId, name } = action.payload
    const authState = yield select(selectAuth)
    if (!authState) return // already updated by reducer

    try {
        const projectRef = doc(db, 'projects', projectId)

        yield call(() => updateDoc(projectRef, { name, updatedAt: serverTimestamp() }))
    } catch (err: any) {
        if (err?.code === 'permission-denied') showToast("Ask the owner for access", { type: 'error' })
        else console.error('Error updating project name:', err)
    }
}

// Update project description
function* updateProjectDescriptionWorker(action: PayloadAction<{ projectId: string; description: string }>): Generator<any, void, any> {
    const { projectId, description } = action.payload
    const authState = yield select(selectAuth)
    if (!authState) return // already updated by reducer

    if (description === undefined) return

    try {
        const cleanDescription = JSON.parse(JSON.stringify(description))
        const projectRef = doc(db, 'projects', projectId)
        yield call(() => updateDoc(projectRef, { description: cleanDescription, updatedAt: serverTimestamp(), updatedBy: authState.id }))
    } catch (err: any) {
        if (err?.code === 'permission-denied') showToast("Ask the owner for access", { type: 'error' })
        else console.error('Error updating project description:', err)
    }
}

// Delete project
function* deleteProjectWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const projectId = action.payload

    try {
        const workspace = yield select(selectWorkspace)
        history.push(`/${workspace?.workspaceUrlId}/projects`)

        const projectRef = doc(db, 'projects', projectId)
        yield call(deleteDoc, projectRef)

        showToast('Project deleted')
    } catch (err) {
        console.error('Error deleting project:', err)
    }
}

// Generate an unguessable, URL-safe share token (~22 chars of base64url).
function generateShareToken(): string {
    const bytes = new Uint8Array(16)
    crypto.getRandomValues(bytes)
    let binary = ''
    for (const b of bytes) binary += String.fromCharCode(b)
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// Publish a project: mint a fresh share token, flip isPublic on, open the share dialog.
function* publishProjectWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const projectId = action.payload
    yield put(addLoadingKey('publish-project'))
    try {
        const token = generateShareToken()
        const projectRef = doc(db, 'projects', projectId)
        yield call(() => updateDoc(projectRef, {
            isPublic: true,
            publicShareId: token,
            updatedAt: serverTimestamp()
        }))
        // Activation: a share link went live (fired after the successful write; the
        // share token itself never goes to analytics).
        const workspace = yield select(selectWorkspace)
        trackEvent(AnalyticsEvent.SharePublished, { workspace_id: workspace?.id, project_id: projectId })
        yield put(setOpenModal({ type: 'publishProject', projectId }))
    } catch (err: any) {
        if (err?.code === 'permission-denied') showToast("Ask the owner for access", { type: 'error' })
        else console.error('Error publishing project:', err)
    } finally {
        yield put(removeLoadingKey('publish-project'))
    }
}

// Unpublish: clear the token entirely so the old link dies permanently.
function* unpublishProjectWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const projectId = action.payload
    yield put(addLoadingKey('unpublish-project'))
    try {
        const projectRef = doc(db, 'projects', projectId)
        yield call(() => updateDoc(projectRef, {
            isPublic: false,
            publicShareId: null,
            updatedAt: serverTimestamp()
        }))
        showToast('Project unpublished')
    } catch (err: any) {
        if (err?.code === 'permission-denied') showToast("Ask the owner for access", { type: 'error' })
        else console.error('Error unpublishing project:', err)
    } finally {
        yield put(removeLoadingKey('unpublish-project'))
    }
}

// Listen to all projects the user is a member of (via shared_members array)
// Also derives memberProjectIds from the same snapshot

export function* listenAllProjects(workspaceId: string): Generator<any, void, any> {
    const authState = yield select(selectAuth)
    const userId: string = authState?.id
    const isOwner: boolean = (authState?.workspaces ?? []).some(
        (w: any) => w.id === workspaceId && w.role === 'owner'
    )

    logTiming(`[projects] listenAllProjects started (workspaceId=${workspaceId}, isOwner=${isOwner})`)
    const projectQuery = isOwner
        ? query(collection(db, 'projects'), where('workspaceId', '==', workspaceId))
        : query(collection(db, 'projects'), where('workspaceId', '==', workspaceId), where('shared_members', 'array-contains', userId))

    const channel: ReturnType<typeof createSnapshotChannel> = yield call(
        createSnapshotChannel,
        projectQuery
    )

    let firstData = true
    try {
        while (true) {
            const event: any = yield take(channel)
            if (event.type === 'data') {
                for (const change of event.snapshot.docChanges()) {
                    if (change.type === 'removed') {
                        yield put(removeProject(change.doc.id))
                    } else {
                        yield put(upsertProject({ id: change.doc.id, ...change.doc.data() } as Project))
                    }
                }
                yield put(setLoading(false))
                if (firstData) {
                    logTiming(`[projects] first snapshot (${event.snapshot.size} docs) → setLoading(false)`)
                    firstData = false
                }

                const allProjects = yield select(selectProjectsById)
                const memberIds: string[] = Object.keys(allProjects)
                yield put(setMemberProjectIds(memberIds))
            } else {
                console.error('[projectsSaga] listenAllProjects: channel error:', event.error)
                yield put(setLoading(false))
            }
        }
    } finally {
        channel.close()
    }
}

function* addMemberWorker(action: PayloadAction<{ projectId: string; workspaceId: string; userId: string }>): Generator<any, void, any> {
    const { projectId, userId } = action.payload

    const authState = yield select(selectAuth)
    if (!authState) {
        const project: Project | undefined = yield select((state: RootState) => selectProjectById(state, projectId))
        if (!project) return
        const members = project.members || []
        if (members.includes(userId)) return
        yield put(upsertProject({ ...project, members: [...members, userId] } as Project))
        return
    }

    try {
        yield call(() => updateDoc(doc(db, 'projects', projectId), {
            members: arrayUnion(userId),
            shared_members: arrayUnion(userId),
            updatedAt: serverTimestamp()
        }))
        showToast('Member added')
    } catch (err: any) {
        if (err?.code === 'permission-denied') showToast("Ask the owner for access", { type: 'error' })
        else console.error('Error adding project member:', err)
    }
}

function* removeMemberWorker(action: PayloadAction<{ projectId: string; userId: string }>): Generator<any, void, any> {
    const { projectId, userId } = action.payload

    const authState = yield select(selectAuth)
    if (!authState) {
        const project: Project | undefined = yield select((state: RootState) => selectProjectById(state, projectId))
        if (!project) return
        const members = (project.members || []).filter(id => id !== userId)
        yield put(upsertProject({ ...project, members } as Project))
        return
    }

    try {
        const project: Project | undefined = yield select((state: RootState) => selectProjectById(state, projectId))
        const workspaceMembers = yield select(selectMembers)

        const isProjectOwner = project?.ownerId === userId
        const isWorkspaceOwner = workspaceMembers.some((m: any) => m.userId === userId && m.role === 'owner')

        const update: Record<string, any> = {
            members: arrayRemove(userId),
            updatedAt: serverTimestamp()
        }

        if (!isProjectOwner && !isWorkspaceOwner) {
            update.shared_members = arrayRemove(userId)
        }

        yield call(() => updateDoc(doc(db, 'projects', projectId), update))
        showToast('Member removed')
    } catch (err: any) {
        if (err?.code === 'permission-denied') showToast("Ask the owner for access", { type: 'error' })
        else console.error('Error removing project member:', err)
    }
}

function* addToProjectMembers(projectId: string, userId: string): Generator<any, boolean, any> {
    const project: Project | undefined = yield select((state: RootState) => selectProjectById(state, projectId))
    if ((project?.members || []).includes(userId)) {
        yield put(setProjectInviteError('Already a project member'))
        return false
    }
    yield call(() => updateDoc(doc(db, 'projects', projectId), {
        members: arrayUnion(userId),
        updatedAt: serverTimestamp()
    }))
    return true
}

function* inviteToProjectWorker(action: PayloadAction<{ projectId: string; workspaceId: string; email: string; clearInput?: () => void }>): Generator<any, void, any> {
    const { projectId, workspaceId, email, clearInput } = action.payload

    yield put(setProjectInviteLoading(true))
    yield put(setProjectInviteError(null))

    try {
        const inviteMember = httpsCallable<
            { workspaceId: string; email: string },
            { status: string; userId?: string; invitationId?: string }
        >(functions, 'inviteMember')

        const result: { data: { status: string; userId?: string; invitationId?: string } } = yield call(
            () => inviteMember({ workspaceId, email })
        )

        let memberUserId: string
        if (result.data.status === 'added' && result.data.userId) {
            memberUserId = result.data.userId
        } else if (result.data.status === 'invited' && result.data.invitationId) {
            memberUserId = result.data.invitationId
        } else {
            throw new Error('Unexpected response from inviteMember')
        }

        const added = yield call(addToProjectMembers, projectId, memberUserId)
        if (added) {
            // Activation funnel: invite sent. The person property powers the solo
            // vs team retention split in PostHog (the core solo-entry check).
            trackEvent(AnalyticsEvent.InviteSent, { project_invite: true })
            setPersonProps({ invited_teammate: true })
            clearInput?.()
            showToast('Invitation sent')
        }
    } catch (err: any) {
        console.error('Error inviting to project:', err)
        if (err.code === 'functions/already-exists') {
            const normalizedEmail = email.toLowerCase().trim()
            const workspaceMembers = yield select(selectMembers)
            const existingMember = workspaceMembers.find((m: any) => m.email === normalizedEmail)

            if (existingMember) {
                const added = yield call(addToProjectMembers, projectId, existingMember.userId)
                if (added) clearInput?.()
            } else {
                yield put(setProjectInviteError('Already invited to workspace'))
            }
        } else {
            yield put(setProjectInviteError(err.message || 'Failed to invite'))
        }
    } finally {
        yield put(setProjectInviteLoading(false))
    }
}

function* updateProjectOwnerWorker(action: PayloadAction<{ projectId: string; ownerId: string }>): Generator<any, void, any> {
    const { projectId, ownerId } = action.payload
    try {
        const project: Project | undefined = yield select((state: RootState) => selectProjectById(state, projectId))
        if (!project) return

        const previousOwnerId: string = project.ownerId
        const members: string[] = project.members || []
        const workspaceMembers = yield select(selectMembers)

        const projectRef = doc(db, 'projects', projectId)
        const batch = writeBatch(db)

        batch.update(projectRef, {
            ownerId,
            shared_members: arrayUnion(ownerId),
            updatedAt: serverTimestamp()
        })

        if (previousOwnerId !== ownerId && !members.includes(previousOwnerId)) {
            const isWorkspaceOwner = workspaceMembers.some(
                (m: any) => m.userId === previousOwnerId && m.role === 'owner'
            )
            if (!isWorkspaceOwner) {
                batch.update(projectRef, { shared_members: arrayRemove(previousOwnerId) })
            }
        }

        yield call([batch, batch.commit])
        showToast('Project owner updated')
    } catch (err: any) {
        if (err?.code === 'permission-denied') showToast("Ask the owner for access", { type: 'error' })
        else console.error('Error updating project owner:', err)
    }
}

export function* watchProjectsSaga() {
    yield takeLatest(createProjectAction.type, createProjectWorker)
    yield takeLatest(updateProjectNameSaga.type, updateProjectNameWorker)
    yield takeLatest(updateProjectDescriptionSaga.type, updateProjectDescriptionWorker)
    yield takeLatest(deleteProjectSaga.type, deleteProjectWorker)
    yield takeLatest(publishProjectSaga.type, publishProjectWorker)
    yield takeLatest(unpublishProjectSaga.type, unpublishProjectWorker)
    yield takeEvery(addMemberSaga.type, addMemberWorker)
    yield takeEvery(removeMemberSaga.type, removeMemberWorker)
    yield takeEvery(inviteToProjectSaga.type, inviteToProjectWorker)
    yield takeEvery(updateProjectOwnerSaga.type, updateProjectOwnerWorker)
}
